/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useMemo } from "react";
import type { FunctionComponent, JSX, MouseEvent } from "react";

// Imported per component rather than through the `views/common` barrel, which
// also re-exports connected containers — see recordingHighlightActionCatalog.
import { ImageLoader } from "../../common/ImageLoader";
import MyHubMovieInfoCard from "./MyHubMovieInfoCard";
import MyHubSeriesInfoCard from "./MyHubSeriesInfoCard";
import { toRecordingHighlightActions } from "./recordingHighlightActionCatalog";
import type { TRecordingHighlightAction } from "./recordingHighlightActionCatalog";
import type { MyHubRecordingHighlightProps } from "./MyHubRecordingHighlightProps";
import "./MyHubRecordingHighlight.scss";

/**
 * MyHubRecordingHighlight — the popup shown for a *single* explicit recording or
 * movie (Figma: Unity Design System › InfoCards — Type=Movie 1961:24411,
 * Type=Series 1961:24821). The sheet both cards share: a landscape thumbnail
 * bleeding off the top-right corner behind a text column, and the row of
 * action buttons under it. The text block itself is the item's info card —
 * {@link MyHubMovieInfoCard} for a movie, {@link MyHubSeriesInfoCard} for
 * everything else.
 *
 * Presentational and stateless. Focus arrives as `focusedActionIndex` and D-pad
 * input is routed by `MyHubRecordingHighlightController` on the modal stack, so
 * the pointer and D-pad paths paint from the same state. Because the container
 * is mounted once in the app shell, this popup can be raised over any screen
 * without that screen hosting it.
 */
const MyHubRecordingHighlight: FunctionComponent<
  MyHubRecordingHighlightProps
> = (props: MyHubRecordingHighlightProps): JSX.Element => {
  // a. TAG
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09) for logging context
  const TAG: string = "MyHubRecordingHighlight";

  // b. Local constants
  const {
    item,
    isClosing,
    focusedActionIndex,
    onActionFocus,
    onActionSelect,
    onDismiss
  } = props;

  // c. Local shared variables
  const hasBackdrop: boolean = item.backdropImageUrl !== "";
  const isMovie: boolean = item.cardType === "movie";

  // The dismiss transition is the rise reversed, so it is painted the same way
  // the rest of this component paints state: a modifier the CSS animates from.
  // The root modifier is what stops a sinking sheet from catching a click meant
  // for the screen behind it.
  const rootClassName: string =
    "my-hub-recording-highlight" +
    (isClosing ? " my-hub-recording-highlight--closing" : "");
  // The card type is a panel modifier too: the movie card sits on the sheet's
  // bottom padding where the series card is centred in it.
  const panelClassName: string =
    "my-hub-recording-highlight__panel" +
    (isMovie
      ? " my-hub-recording-highlight__panel--movie"
      : " my-hub-recording-highlight__panel--series") +
    (isClosing ? " my-hub-recording-highlight__panel--closing" : "");

  // Labels are locale lookups, so this is rebuilt only when the popup re-renders
  // for a different item rather than on every focus move.
  const actions: TRecordingHighlightAction[] = useMemo(
    (): TRecordingHighlightAction[] =>
      toRecordingHighlightActions(item.availableActions),
    [item.availableActions]
  );

  // g. Internal functions
  const renderAction = (
    action: TRecordingHighlightAction,
    index: number
  ): JSX.Element => {
    const isFocused: boolean = index === focusedActionIndex;
    const className: string =
      "my-hub-recording-highlight__action" +
      (isFocused ? " my-hub-recording-highlight__action--focused" : "");

    return (
      <button
        key={action.id}
        className={className}
        type="button"
        // The panel is the popup's single tab stop; Left/Right between buttons
        // is routed by the modal controller, never by the browser's tab order.
        tabIndex={-1}
        data-testid={`my-hub-recording-highlight-action-${action.id}`}
        // Reported on movement rather than `mouseenter`: the popup animates into
        // place, which slides a button under a resting cursor and would
        // otherwise steal focus from the button the D-pad had selected.
        onMouseMove={(): void => {
          if (!isFocused) {
            onActionFocus(index);
          }
        }}
        onClick={(event: MouseEvent<HTMLButtonElement>): void => {
          // Keep the tap off the dimmer sitting behind the panel.
          event.stopPropagation();
          onActionSelect(action.id);
        }}
      >
        {action.icon}
        <span className="my-hub-recording-highlight__action-label">
          {action.label}
        </span>
      </button>
    );
  };

  // i. Return JSX
  return (
    <div className={rootClassName} data-testid="my-hub-recording-highlight">
      {/*
        Pointer-only dismissal. BACK is handled by the modal controller, so this
        needs no keyboard handler and is marked aria-hidden — it duplicates an
        affordance the controller already exposes (SonarQube S6848 / S1082).
      */}
      <div
        className="my-hub-recording-highlight__dimmer"
        aria-hidden="true"
        role="presentation"
        onClick={onDismiss}
      />
      <section
        className={panelClassName}
        role="dialog"
        aria-modal="true"
        aria-label={item.title}
      >
        <div className="my-hub-recording-highlight__backdrop">
          {hasBackdrop && (
            <ImageLoader
              // The landscape crop, not `item.imageUrl`'s (usually portrait)
              // one — requested at the backdrop's own aspect ratio so
              // `object-fit: cover` below has a source shaped like the box
              // it's shown in, rather than stretching or over-cropping a
              // mismatched poster (see `backdropImageUrl`'s own comment).
              src={item.backdropImageUrl}
              fallbackText={item.title}
              classStyle="my-hub-recording-highlight__backdrop-image"
            />
          )}
          {/* Fades the thumbnail into the panel's own fill on both edges it
              doesn't bleed off — the CSS the Figma export gives for this
              node is two stacked gradients over the art, reproduced here as
              one overlay rather than a `background` shorthand so the image
              can stay a normal `src` the ImageLoader owns (retry/fallback
              included). */}
          <div className="my-hub-recording-highlight__backdrop-fade" />
        </div>

        <div className="my-hub-recording-highlight__content">
          {isMovie ? (
            <MyHubMovieInfoCard item={item} />
          ) : (
            <MyHubSeriesInfoCard item={item} />
          )}

          <div className="my-hub-recording-highlight__actions">
            {actions.map(
              (action: TRecordingHighlightAction, index: number): JSX.Element =>
                renderAction(action, index)
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyHubRecordingHighlight;
